import React, { useEffect, useMemo, useState } from 'react'
import { SelectFramesStyles } from 'ui/utils/SelectFrame'
import { Text1 } from 'ui/shorthands/Texts'
import { StartEnd } from 'ui/utils/AvailableDateTimes'
import { getMinutes, getHours, addDays } from 'date-fns'
import { formatDateString } from 'ui/utils/dateFormatUtils'
import { buildStartEndList } from 'ui/utils/AvailableDateTimes'
import { ReservationFrameUnitMin } from 'types/common'
import { jstNewDate, isJstToday } from 'ui/utils/jstDatetime'

function getStartMin(
  currentMin: number,
  unit: ReservationFrameUnitMin
): string {
  if (currentMin < 15) {
    return '00'
  } else if (currentMin < 30) {
    switch (unit) {
      case 15:
        return '15'
      case 30:
        return '00'
    }
    return '15'
  } else if (currentMin < 45) {
    return '30'
  } else {
    switch (unit) {
      case 15:
        return '45'
      case 30:
        return '30'
    }
  }
}

export type FutureReservationFramesProps = {
  date: Date
  onSelect: (data: {
    start_datetime: string
    end_datetime: string
  }) => Promise<void> | void
  unit: ReservationFrameUnitMin
  now?: Date
}

const FutureReservationFrames: React.FC<FutureReservationFramesProps> = ({
  date,
  onSelect,
  unit,
  now = jstNewDate,
}) => {
  const { classes, cx } = SelectFramesStyles()

  const frameHeight = 56

  const framesRef = React.createRef<HTMLDivElement>()

  const [selectedIndex, setSelectedIndex] = useState<number | undefined>()
  const [selectedDateIsToday, setSelectedDateIsToday] = useState<boolean>(true)
  const hour = getHours(now)
  const currentMin = getMinutes(now)
  const minutes = getStartMin(currentMin, unit)

  const frames: StartEnd[] = useMemo(() => {
    // NOTE: 現在の時,分による調整、今日以外は明日以降の全ての時間帯が選択できる
    if (selectedDateIsToday) {
      return buildStartEndList({
        from: `${hour}:${minutes}`,
        to: '24:00',
        unit,
      })
    }
    return buildStartEndList({
      from: '00:00',
      to: '24:00',
      unit,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDateIsToday, unit, now])

  useEffect(() => {
    setIndexHandler(undefined)
    date && setSelectedDateIsToday(isJstToday(date))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date])

  const setIndexHandler = (index: number | undefined) => {
    if (typeof index === 'undefined' || !frames[index]) return
    const { start, end } = frames[index]
    selectHandler(start, end)
    setSelectedIndex(index)
  }

  const onSwitchIndexHandler = (index: number) => {
    const scroll = framesRef?.current
    if (scroll) {
      scroll.scrollTop = frameHeight * (index >= 1 ? index - 1 : 0)
    }
    setIndexHandler(index)
  }

  useEffect(() => {
    // NOTE: unitを切り替えた時の選択状態が自然な感じで切り替わるようにindexの値を調整している
    if (typeof selectedIndex === 'number') {
      // unit15の場合の選択肢の最初に15分または45分の項目があるかどうかをチェック
      const unit15StartMin = getStartMin(currentMin, 15)
      const unit15startHas15or45Min =
        unit15StartMin === '15' || unit15StartMin === '45'
      const indexIsOdd = selectedIndex % 2 === 1
      const addIndex =
        selectedIndex && unit15startHas15or45Min && indexIsOdd ? 1 : 0

      let targetIndex
      switch (unit) {
        case 30:
          targetIndex =
            Math.round(
              selectedIndex % 2 === 0
                ? selectedIndex / 2
                : (selectedIndex - 1) / 2
            ) + addIndex
          break
        case 15:
          targetIndex = selectedIndex * 2 - addIndex
          break
      }
      onSwitchIndexHandler(targetIndex)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit])

  const selectHandler = (start: string, end: string) => {
    let dateString = formatDateString(date)
    let endDateString = dateString
    if (end === '24:00') {
      endDateString = formatDateString(addDays(date, 1))
      end = '00:00'
    }
    onSelect({
      start_datetime: `${dateString} ${start}:00`,
      end_datetime: `${endDateString} ${end}:00`,
    })
  }

  return (
    <div className={classes.root} ref={framesRef}>
      {frames.map(({ start, end }, index) => (
        <div
          key={`${start}-${end}-${index}`}
          onClick={() => {
            setIndexHandler(index)
          }}
          className={cx(
            classes.item,
            classes.available,
            selectedIndex === index && classes.selected
          )}
        >
          <Text1
            className={cx(selectedIndex === index && classes.selectedText)}
          >
            {start}〜{end}
          </Text1>
        </div>
      ))}
    </div>
  )
}
export default FutureReservationFrames
